import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CreditCard, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiError, type BillingMe } from "@/lib/api";
import { STATUS_LABELS, formatDate, mxn, openPortal } from "@/lib/billing";
import { SettingsSection } from "./SettingsSection";
import { GuaranteeBox } from "./GuaranteeBox";

function Row({ label, value, testId }: { label: string; value: string; testId: string }) {
  return <div className="rounded-xl bg-[#F5F6F8] p-4"><p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p><p className="mt-1 font-semibold text-[#1B2A4A]" data-testid={testId}>{value}</p></div>;
}

export function SubscriptionCard() {
  const [b, setB] = useState<BillingMe | null>(null);
  const [busy, setBusy] = useState(false);
  const load = () => { api.get<BillingMe>("/billing/me").then((r) => setB(r.data)).catch(() => undefined); };
  useEffect(load, []);
  const portal = async () => { setBusy(true); try { await openPortal(); } catch (e) { toast.error(e instanceof Error && e.message === "popup" ? "Tu navegador bloqueó la ventana. Permite ventanas emergentes para este sitio." : formatApiError(e)); } finally { setBusy(false); } };
  const plan = b?.plan_interval ? (b.plan_interval === "annual" ? "Anual" : `Mensual · ${mxn(b.plans.monthly)}`) : "—";
  const dateLabel = b?.status === "trialing" ? "Fin de la prueba" : b?.status === "canceling" || b?.status === "expired" ? "Acceso hasta" : "Próximo cobro";
  const date = b?.status === "trialing" ? b.trial_end : b?.current_period_end;

  return (
    <SettingsSection id="suscripcion" icon={CreditCard} title="Suscripción" desc="Tu plan, próxima fecha de cobro y portal de pagos.">
      {!b ? <div className="h-24 animate-pulse rounded-xl bg-[#F5F6F8]" /> : b.status === "exempt" ? (
        <p className="text-sm text-slate-600" data-testid="subscription-exempt">Esta cuenta no necesita suscripción.</p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-3">
            <Row label="Plan" value={plan} testId="subscription-plan" />
            <Row label="Estado" value={STATUS_LABELS[b.status]} testId="subscription-status" />
            <Row label={dateLabel} value={formatDate(date)} testId="subscription-date" />
          </div>
          {b.pending_plan_interval && (
            <p className="mt-3 rounded-lg bg-[#FFFBEB] px-3 py-2 text-sm text-amber-900" data-testid="subscription-pending-change">
              Cambio programado: el {formatDate(b.pending_change_at)} pasas al plan {b.pending_plan_interval === "annual" ? "anual" : `mensual (${mxn(b.plans.monthly)})`}.
            </p>
          )}
          <GuaranteeBox b={b} onDone={load} />
          <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-slate-500">Cambia tu tarjeta, ve tus recibos, cámbiate al plan anual de $3,000 MXN o cancela. Si cancelas, conservas el acceso hasta el fin del periodo pagado.</p>
            {b.status === "none" || b.status === "expired" ? (
              <Link to="/suscripcion" className="inline-flex h-11 shrink-0 items-center justify-center rounded-md bg-[#1B2A4A] px-5 text-sm font-semibold text-white" data-testid="subscription-plans-link">{b.status === "expired" ? "Reactivar" : "Elegir plan"}</Link>
            ) : (
              <Button onClick={portal} disabled={busy} className="h-11 shrink-0 bg-[#1B2A4A] text-white hover:bg-[#2A3D63]" data-testid="subscription-portal-button">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ExternalLink className="mr-2 h-4 w-4" /> Administrar mi suscripción</>}
              </Button>
            )}
          </div>
        </>
      )}
    </SettingsSection>
  );
}
